import React, { useState, useEffect } from 'react';
import {
    Box, Typography, CircularProgress, Paper, TextField, Button, Chip
} from '@mui/material';
import { Description, Save } from '@mui/icons-material';
import { getRequest, postRequest, showMessage } from '../../../config';
import AnimatedPage from '../../../components/AnimatedPage';

const PROMPT_TYPE = 'consolidated-diligence-report';

const PROMPT_SECTIONS = [
    {
        key: 'system-prompt',
        label: 'System Prompt',
        description: 'Shared system prompt used for all section summaries (Fit, Directors, Research, etc.)'
    },
    {
        key: 'fit-assessment',
        label: 'Fit Assessment',
        description: 'Instructions for generating the Fit Assessment section (300-400 words)'
    },
    {
        key: 'director-profiles',
        label: 'Director Profiles',
        description: 'Instructions for generating each director profile (80-120 words each, max 10 directors)'
    },
    {
        key: 'research-reports',
        label: 'Research Reports',
        description: 'Instructions for summarizing each research report (300-400 words each). Use {{reportName}} as placeholder.'
    },
    {
        key: 'investment-readiness',
        label: 'Investment Readiness',
        description: 'Instructions for generating the Investment Readiness section (300-400 words)'
    },
    {
        key: 'data-repository',
        label: 'Data Repository',
        description: 'Instructions for generating the Data Repository summary (500-600 words)'
    },
    {
        key: 'business-audit',
        label: 'Business Audit',
        description: 'Instructions for generating the Business Audit summary (500-600 words)'
    },
    {
        key: 'warranty-disclosures',
        label: 'Warranty Disclosures',
        description: 'Instructions for generating the Warranty Disclosures summary (500-600 words)'
    },
    {
        key: 'executive-summary',
        label: 'Executive Summary',
        description: 'Instructions for generating the Executive Summary (300-400 words). Use {{companyName}} as placeholder.'
    }
];

const DiligenceReportPrompts = () => {
    const [prompts, setPrompts] = useState({});
    const [editedPrompts, setEditedPrompts] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [savingKey, setSavingKey] = useState(null);

    useEffect(() => {
        fetchPrompts();
    }, []);

    const fetchPrompts = async () => {
        try {
            setIsLoading(true);
            const response = await getRequest('/prompts');
            if (response.code === 200) {
                const allPrompts = response.body || [];
                const diligencePrompts = {};
                allPrompts
                    .filter(p => p.promptType === PROMPT_TYPE)
                    .forEach(p => { diligencePrompts[p.promptName] = p; });
                setPrompts(diligencePrompts);

                // Initialize edited state with DB values
                const edited = {};
                PROMPT_SECTIONS.forEach(s => {
                    edited[s.key] = diligencePrompts[s.key]?.promptText || '';
                });
                setEditedPrompts(edited);
            } else {
                showMessage(response.message || 'Failed to fetch prompts');
            }
        } catch (error) {
            console.error('Error fetching prompts:', error);
            showMessage('Error fetching prompts');
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (key, value) => {
        setEditedPrompts(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = async (sectionDef) => {
        const { key } = sectionDef;
        const promptText = editedPrompts[key];
        if (!promptText || !promptText.trim()) {
            showMessage('Prompt text cannot be empty');
            return;
        }

        try {
            setSavingKey(key);
            const existing = prompts[key];
            const formData = {
                promptName: key,
                promptType: PROMPT_TYPE,
                promptText: promptText.trim(),
                isActive: true
            };

            let response;
            if (existing?._id) {
                response = await postRequest(`/prompts/${existing._id}`, formData);
            } else {
                response = await postRequest('/prompts', formData);
            }

            if (response.code === 200) {
                showMessage('Prompt saved successfully');
                setPrompts(prev => ({ ...prev, [key]: response.body }));
            } else {
                showMessage(response.message || 'Failed to save prompt');
            }
        } catch (error) {
            console.error('Error saving prompt:', error);
            showMessage('Error saving prompt');
        } finally {
            setSavingKey(null);
        }
    };

    const hasChanges = (key) => {
        const dbText = prompts[key]?.promptText || '';
        const current = editedPrompts[key] || '';
        return current !== dbText;
    };

    if (isLoading) {
        return (
            <AnimatedPage>
                <Box className="df fdc jcc aic" sx={{ minHeight: '50vh' }}>
                    <CircularProgress sx={{ color: '#4A67FF', mb: 2 }} />
                    <Typography variant="body1" color="textSecondary">Loading prompts...</Typography>
                </Box>
            </AnimatedPage>
        );
    }

    return (
        <AnimatedPage>
            <Box className="df fdc" sx={{ padding: { xs: '16px', sm: '24px', md: '32px' }, minHeight: '100vh', background: 'linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%)' }}>
                <Paper
                    elevation={0}
                    sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #4A67FF 0%, #3956F0 100%)', borderRadius: '16px', color: 'white' }}
                    className="df jcsb aic"
                >
                    <Box className="df aic" sx={{ gap: 2 }}>
                        <Description sx={{ fontSize: 40 }} />
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, color: 'white' }}>
                                Diligence Report Prompts
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9, color: 'white' }}>
                                Manage AI prompts used to generate the Consolidated Diligence Report
                            </Typography>
                        </Box>
                    </Box>
                </Paper>

                <Box className="df fdc" sx={{ gap: 3 }}>
                    {PROMPT_SECTIONS.map((section) => (
                        <Paper
                            key={section.key}
                            elevation={0}
                            sx={{ p: 3, borderRadius: '12px', border: '1px solid #e0e0e0', background: 'white' }}
                        >
                            <Box className="df jcsb aic" sx={{ mb: 2 }}>
                                <Box>
                                    <Box className="df aic" sx={{ gap: 1, mb: 0.5 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {section.label}
                                        </Typography>
                                        {hasChanges(section.key) && (
                                            <Chip label="Unsaved Changes" size="small" color="info" variant="filled" />
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="textSecondary">
                                        {section.description}
                                    </Typography>
                                </Box>
                            </Box>

                            <TextField
                                fullWidth
                                multiline
                                minRows={4}
                                maxRows={12}
                                value={editedPrompts[section.key] || ''}
                                onChange={(e) => handleChange(section.key, e.target.value)}
                                variant="outlined"
                                placeholder={`Please add a prompt for ${section.label}.`}
                                sx={{
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                        fontFamily: 'monospace',
                                        fontSize: '0.875rem',
                                        lineHeight: 1.6
                                    }
                                }}
                            />

                            <Box className="df" sx={{ gap: 1, justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={savingKey === section.key ? <CircularProgress size={16} color="inherit" /> : <Save />}
                                    onClick={() => handleSave(section)}
                                    disabled={savingKey === section.key || !hasChanges(section.key)}
                                    sx={{ textTransform: 'none', background: '#4A67FF', '&:hover': { background: '#3956F0' } }}
                                >
                                    {savingKey === section.key ? 'Saving...' : 'Save'}
                                </Button>
                            </Box>
                        </Paper>
                    ))}
                </Box>
            </Box>
        </AnimatedPage>
    );
};

export default DiligenceReportPrompts;
